/* ==========================================================================
   Accessibility enhancements (WCAG 2.2 AA).
   Layered on top of the Minimog theme scripts: it watches the classes the
   theme already toggles and adds ARIA state, focus management and keyboard
   support, so the vendor JS stays untouched.
   ========================================================================== */
(function () {
  "use strict";

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  function isVisible(el) {
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    return getComputedStyle(el).visibility !== "hidden";
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return !el.closest("[inert]") && isVisible(el);
    });
  }

  function onAttrChange(el, callback) {
    new MutationObserver(callback).observe(el, { attributes: true, attributeFilter: ["class", "style"] });
  }

  function inOtherDialog(el, own) {
    var dialog = el && el.closest && el.closest('[role="dialog"], [aria-modal="true"], dialog');
    return !!dialog && !own.contains(dialog) && !dialog.contains(own);
  }

  // Remember the last focused element so focus can return to whatever opened a panel.
  var lastFocused = null;
  // And the last one that was not already inside a panel. Some drawers take
  // focus the moment they open, before anything here notices them, which leaves
  // lastFocused pointing into the drawer itself — useless for getting back out.
  var lastFocusedOutsidePanel = null;
  document.addEventListener(
    "focusin",
    function (e) {
      lastFocused = e.target;
      if (!e.target.closest || !e.target.closest('[role="dialog"]')) lastFocusedOutsidePanel = e.target;
    },
    true
  );

  /* Live-region announcements: a11yAnnounce("Added to cart"), a11yAnnounce("Sold out", true) for errors */
  window.a11yAnnounce = function (message, urgent) {
    var region = document.getElementById(urgent ? "a11y-alert" : "a11y-status");
    if (!region) return;
    region.textContent = "";
    setTimeout(function () {
      region.textContent = message;
    }, 100);
  };

  /* --------------------------------------------------------------------------
     Overlay controller: when the theme opens a panel, move focus into it, keep
     Tab inside it, close on Escape, and restore focus when it closes.
     -------------------------------------------------------------------------- */
  function manageOverlay(opts) {
    var open = false;
    var opener = null;

    function onKeydown(e) {
      var active = document.activeElement;
      if (inOtherDialog(active, opts.root)) return; // e.g. a cart drawer opened on top

      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        opts.close();
        return;
      }
      if (e.key !== "Tab") return;

      // Move through the panel's own list explicitly: its DOM order can differ
      // from the visual order (e.g. the menu button lives outside the drawer).
      var items = opts.tabbables();
      if (!items.length) return;
      e.preventDefault();
      var index = items.indexOf(active);
      var next;
      if (index === -1) next = e.shiftKey ? items.length - 1 : 0;
      else next = (index + (e.shiftKey ? -1 : 1) + items.length) % items.length;
      items[next].focus();
    }

    // Panels animate in, so their contents can stay visibility:hidden for a moment.
    function focusWhenReady(attempt) {
      if (!opts.isOpen()) return;
      var target = opts.initialFocus();
      if (target && isVisible(target)) {
        // preventScroll: panels that slide in from the side would otherwise drag
        // their container sideways to reveal the element
        target.focus({ preventScroll: true });
      } else if (attempt < 15) {
        setTimeout(function () {
          focusWhenReady(attempt + 1);
        }, 50);
      }
    }

    function sync() {
      var now = opts.isOpen();
      if (now === open) return;
      open = now;

      if (open) {
        opener = lastFocused;
        document.addEventListener("keydown", onKeydown, true);
        if (opts.onChange) opts.onChange(true);
        setTimeout(function () {
          focusWhenReady(0);
        }, opts.focusDelay || 60);
      } else {
        document.removeEventListener("keydown", onKeydown, true);
        if (opts.onChange) opts.onChange(false);
        var active = document.activeElement;
        var focusLost = !active || active === document.body || opts.root.contains(active);
        if (focusLost && opener && document.contains(opener) && isVisible(opener)) opener.focus();
      }
    }

    // Marks this panel as one we already manage, so the app-drawer watcher below
    // doesn't try to handle it a second time.
    opts.root.setAttribute("data-a11y-overlay", "");

    opts.watch.forEach(function (el) {
      onAttrChange(el, sync);
    });
  }

  /* --------------------------------------------------------------------------
     Mobile menu drawer (G1, 1.4)
     -------------------------------------------------------------------------- */
  function initMobileMenu() {
    var button = document.querySelector("button.m-hamburger-box");
    var drawer = document.getElementById("m-menu-drawer");
    if (!button || !drawer) return;

    var content = drawer.querySelector(".m-menu-drawer__content");

    // Moving focus to something in a panel that is still parked to one side
    // makes the browser scroll the drawer sideways to reach it, leaving the
    // open panel hanging off the left edge. Keep the drawer pinned at zero.
    [drawer, content].forEach(function (el) {
      if (!el) return;
      el.addEventListener(
        "scroll",
        function () {
          if (el.scrollLeft !== 0) el.scrollLeft = 0;
        },
        { passive: true }
      );
    });

    manageOverlay({
      root: drawer,
      watch: [drawer],
      isOpen: function () {
        return drawer.classList.contains("open");
      },
      tabbables: function () {
        return [button].concat(focusables(drawer));
      },
      initialFocus: function () {
        return drawer.querySelector(".m-menu-mobile__link");
      },
      close: function () {
        if (drawer.classList.contains("open")) button.click();
      },
      onChange: function (isOpen) {
        button.setAttribute("aria-expanded", String(isOpen));
      },
    });

    // Sub-panels: expanded state, focus the Back button, and make everything the
    // panel covers inert so Tab cannot reach hidden links.
    Array.prototype.forEach.call(drawer.querySelectorAll(".m-megamenu-mobile"), function (panel) {
      var item = panel.parentElement;
      var toggle = item.querySelector(":scope > button.m-menu-mobile__toggle-button");
      var wasOpen = false;
      var inerted = [];

      onAttrChange(panel, function () {
        var isOpen = panel.classList.contains("open");
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        if (toggle) toggle.setAttribute("aria-expanded", String(isOpen));

        if (isOpen) {
          for (var node = panel; node && node !== content && node.parentElement; node = node.parentElement) {
            Array.prototype.forEach.call(node.parentElement.children, function (sibling) {
              if (sibling !== node && !sibling.inert) {
                sibling.inert = true;
                inerted.push(sibling);
              }
            });
          }
          var back = panel.querySelector(".m-menu-mobile__back-button");
          (function focusBack(attempt) {
            if (!back || !panel.classList.contains("open")) return;
            if (isVisible(back)) back.focus({ preventScroll: true });
            else if (attempt < 15) setTimeout(function () { focusBack(attempt + 1); }, 50);
          })(0);
        } else {
          inerted.forEach(function (el) {
            el.inert = false;
          });
          inerted = [];
          var active = document.activeElement;
          if (drawer.classList.contains("open") && toggle && (!active || active === document.body || panel.contains(active))) {
            toggle.focus({ preventScroll: true });
          }
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Desktop drop-down menus (G3): disclosure buttons, Escape, focus-out close
     -------------------------------------------------------------------------- */
  function initDesktopMenu() {
    var toggles = document.querySelectorAll(".m-header__menu button.m-menu__toggle");

    Array.prototype.forEach.call(toggles, function (toggle) {
      var item = toggle.closest(".m-menu__item");
      var header = toggle.closest("m-header");

      function setOpen(isOpen) {
        item.classList.toggle("a11y-open", isOpen);
        toggle.setAttribute("aria-expanded", String(isOpen));
        if (isOpen) item.classList.remove("a11y-dismissed");
        if (header && item.classList.contains("m-menu__item--mega")) header.classList.toggle("m-mega-active", isOpen);
      }
      item.a11ySetOpen = setOpen;

      toggle.addEventListener("click", function (e) {
        e.preventDefault();
        var isOpen = !item.classList.contains("a11y-open");
        Array.prototype.forEach.call(document.querySelectorAll(".m-menu__item.a11y-open"), function (other) {
          if (other !== item && other.a11ySetOpen) other.a11ySetOpen(false);
        });
        setOpen(isOpen);
      });

      item.addEventListener("focusout", function (e) {
        if (item.classList.contains("a11y-open") && !item.contains(e.relatedTarget)) setOpen(false);
      });

      item.addEventListener("keydown", function (e) {
        if ((e.key === "Escape" || e.key === "Esc") && item.classList.contains("a11y-open")) {
          setOpen(false);
          toggle.focus();
        }
      });

      item.addEventListener("mouseleave", function () {
        item.classList.remove("a11y-dismissed");
      });
    });

    // Escape also dismisses menus opened by mouse hover (1.4.13)
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      Array.prototype.forEach.call(document.querySelectorAll(".m-header__menu .m-menu__item--parent:hover"), function (item) {
        item.classList.add("a11y-dismissed");
        item.classList.remove("m-menu__item--active");
        var header = item.closest("m-header");
        if (header) header.classList.remove("m-mega-active");
      });
    });
  }

  /* --------------------------------------------------------------------------
     Search popup (G2, 7.3): modal focus handling
     -------------------------------------------------------------------------- */
  function initSearchPopup() {
    var popup = document.querySelector("[data-search-popup]");
    if (!popup) return;
    var container = popup.querySelector("[data-search-container]");
    if (!container) return;

    manageOverlay({
      root: popup,
      watch: [container],
      focusDelay: 400,
      isOpen: function () {
        return container.classList.contains("m-show-search");
      },
      tabbables: function () {
        return focusables(popup);
      },
      initialFocus: function () {
        return popup.querySelector("[data-search-input]");
      },
      close: function () {
        var host = document.querySelector("m-search-popup[open]") || document.querySelector("m-search-popup");
        if (host && typeof host.closeSearchPopup === "function") host.closeSearchPopup();
      },
      onChange: function (isOpen) {
        Array.prototype.forEach.call(document.querySelectorAll("m-search-popup button"), function (btn) {
          if (btn.hasAttribute("aria-haspopup")) btn.setAttribute("aria-expanded", String(isOpen));
        });
      },
    });
  }

  /* --------------------------------------------------------------------------
     Theme accordions (collection filters, footer, etc.): keyboard + state (2.1.1, 4.1.2)
     -------------------------------------------------------------------------- */
  function initAccordions(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll(".m-accordion--item-button"), function (header) {
      if (header.dataset.a11yAccordion) return;
      header.dataset.a11yAccordion = "true";
      var item = header.closest(".m-accordion--item");
      var content = item && item.querySelector(".m-accordion--item-content");
      if (!item) return;

      // Keep heading semantics: put the button role on an inner wrapper instead
      var control = header;
      if (/^H[1-6]$/.test(header.tagName)) {
        control = document.createElement("span");
        control.className = "a11y-accordion-control";
        while (header.firstChild) control.appendChild(header.firstChild);
        header.appendChild(control);
      }
      control.setAttribute("role", "button");
      control.setAttribute("tabindex", "0");
      if (content) {
        if (!content.id) content.id = "a11y-acc-" + Math.random().toString(36).slice(2, 9);
        control.setAttribute("aria-controls", content.id);
      }

      var wasOpen = item.classList.contains("open");
      var sync = function () {
        var isOpen = item.classList.contains("open");
        control.setAttribute("aria-expanded", String(isOpen));
        // A panel opened near the bottom of the window unfolds off screen, so
        // the person who just opened it sees nothing happen. Once it has
        // finished expanding, place the heading and its text together in the
        // window: centred when the whole thing fits, otherwise starting just
        // below the sticky header so it can be read from the beginning.
        if (isOpen && !wasOpen && content) {
          revealPanel(header, content, function () {
            return item.classList.contains("open");
          });
        }
        wasOpen = isOpen;
      };
      sync();
      onAttrChange(item, sync);

      control.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          control.click();
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Announce cart changes made over fetch (add to cart, quantity, remove) (4.1.3)
     -------------------------------------------------------------------------- */
  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || undefined, { style: "currency", currency: currency });
    } catch (err) {
      return (cents / 100).toFixed(2);
    }
  }

  function initCartAnnouncements() {
    if (!window.fetch || window.fetch.a11yWrapped) return;
    var originalFetch = window.fetch;
    // Cart apps send follow-up /cart/update calls right after an add; don't let their
    // generic "Cart updated" message replace "… added to cart".
    var lastAddAt = 0;

    var wrapped = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var request = originalFetch.apply(this, arguments);
      var match = /\/cart\/(add|change|update)(\.js)?(\?|$)/.exec(url);
      if (match) {
        request
          .then(function (response) {
            return response.clone().json();
          })
          .then(function (data) {
            if (!data) return;
            if (data.status && (data.description || data.message)) {
              window.a11yAnnounce(data.description || data.message, true);
            } else if (match[1] === "add") {
              lastAddAt = Date.now();
              var item = data.items ? data.items[0] : data;
              var name = item && (item.product_title || item.title);
              window.a11yAnnounce(name ? name + " added to cart" : "Added to cart");
            } else if (typeof data.item_count === "number" && Date.now() - lastAddAt > 4000) {
              window.a11yAnnounce(
                "Cart updated. " + data.item_count + (data.item_count === 1 ? " item" : " items") + ", subtotal " + formatMoney(data.total_price)
              );
            }
          })
          .catch(function () {});
      }
      return request;
    };
    wrapped.a11yWrapped = true;
    window.fetch = wrapped;
  }

  /* Theme toast notifications (errors, limits, "item added") are visual only; announce them too */
  function initThemeNotifications() {
    var theme = window.MinimogTheme;
    var notification = theme && theme.Notification;
    if (!notification || typeof notification.show !== "function" || notification.show.a11yWrapped) return;
    var originalShow = notification.show;
    var wrappedShow = function (options) {
      var result = originalShow.apply(this, arguments);
      if (options && options.message) {
        var tmp = document.createElement("div");
        tmp.innerHTML = String(options.message);
        var text = tmp.textContent.trim();
        if (text) window.a11yAnnounce(text, options.type === "error" || options.type === "warning");
      }
      return result;
    };
    wrappedShow.a11yWrapped = true;
    notification.show = wrappedShow;
  }

  /* Quantity +/- buttons are "disabled" only by a class; expose that state (4.1.2) */
  function syncQuantityButtons(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll(".m-quantity__button"), function (btn) {
      var update = function () {
        btn.setAttribute("aria-disabled", String(btn.disabled || btn.classList.contains("m:disabled")));
      };
      update();
      if (!btn.dataset.a11yQty) {
        btn.dataset.a11yQty = "true";
        onAttrChange(btn, update);
      }
    });
  }

  /* --------------------------------------------------------------------------
     Product page: distinct names for gallery zoom buttons
     -------------------------------------------------------------------------- */
  // Numbering these by their order in the page is wrong wherever the gallery
  // is a looping slider. Such a slider copies slides to make the wrap-around
  // seamless and reorders what it holds, so the first button in the page can
  // belong to the sixth picture, and the count includes copies of pictures the
  // reader has already met. The slider records each slide's real place in
  // data-swiper-slide-index; use that, and count only slides that are not
  // copies. Where there is no slider, position in the page is the real answer.
  function labelZoomButtons(gallery) {
    var buttons = gallery.querySelectorAll(".m-product-media__zoom-in");
    if (!buttons.length) return;
    // Count from the buttons themselves. A gallery can hold a second slider
    // for the thumbnails, so counting every slide in it gives a total from two
    // galleries at once.
    var distinct = 0;
    Array.prototype.forEach.call(buttons, function (btn) {
      var slide = btn.closest(".swiper-slide");
      if (!slide || !slide.classList.contains("swiper-slide-duplicate")) distinct++;
    });
    var total = distinct || buttons.length;
    Array.prototype.forEach.call(buttons, function (btn, i) {
      var slide = btn.closest(".swiper-slide");
      var recorded = slide && slide.getAttribute("data-swiper-slide-index");
      var place = recorded === null || recorded === "" ? i : parseInt(recorded, 10);
      if (isNaN(place)) place = i;
      btn.setAttribute("aria-label", "Zoom in on image " + (place + 1) + " of " + total);
      // A copy exists only to bridge the loop's wrap-around and is only ever
      // on screen for the instant of that transition — the slider always comes
      // to rest on a real slide. Reaching one by keyboard puts the focus ring
      // wherever that copy happens to sit off-canvas (2.4.11), and a screen
      // reader would meet the same picture a second time under the same name.
      // Its real slide is always in the tab order, so nothing is lost by
      // leaving the copy out of it.
      if (slide && slide.classList.contains("swiper-slide-duplicate")) {
        btn.setAttribute("tabindex", "-1");
        slide.setAttribute("aria-hidden", "true");
      } else if (btn.getAttribute("tabindex") === "-1") {
        btn.removeAttribute("tabindex");
      }
    });
  }

  function initProductMedia() {
    Array.prototype.forEach.call(document.querySelectorAll("media-gallery"), function (gallery) {
      labelZoomButtons(gallery);
      // The copies are made after the slider starts, and they carry whatever
      // name was on the button at the time, so label again once it has settled.
      [400, 1200, 2500].forEach(function (delay) {
        setTimeout(function () {
          if (document.contains(gallery)) labelZoomButtons(gallery);
        }, delay);
      });
    });
  }

  /* --------------------------------------------------------------------------
     Cart add-on panels (note, shipping, delivery, discount): state + focus
     -------------------------------------------------------------------------- */
  function initCartAddons() {
    Array.prototype.forEach.call(document.querySelectorAll(".m-cart-addon--trigger-button[aria-controls]"), function (trigger) {
      var panel = document.getElementById(trigger.getAttribute("aria-controls"));
      if (!panel || panel.dataset.a11yAddon) return;
      panel.dataset.a11yAddon = "true";
      var wasOpen = false;

      onAttrChange(panel, function () {
        var isOpen = panel.classList.contains("open");
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        trigger.setAttribute("aria-expanded", String(isOpen));
        if (isOpen) {
          setTimeout(function () {
            var field = panel.querySelector("textarea, input:not([type=hidden]), select");
            if (field && isVisible(field)) field.focus();
          }, 350);
        } else if (panel.contains(document.activeElement) || document.activeElement === document.body) {
          trigger.focus();
        }
      });

      panel.addEventListener("keydown", function (e) {
        if (e.key !== "Escape" && e.key !== "Esc") return;
        var cancel = panel.querySelector(".m-cart-addon--close");
        if (cancel) {
          e.stopPropagation();
          cancel.click();
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Collection filters + load more: keep focus and announce results (2.4.3, 4.1.3)
     -------------------------------------------------------------------------- */
  function initCollectionUpdates() {
    var lastFilter = null;

    document.addEventListener(
      "change",
      function (e) {
        var el = e.target;
        if (!el.closest || !el.closest("#CollectionFiltersForm, collection-filters-form, .m-collection-filters-form")) return;
        // Only what the person actually operated. Moving a price slider makes
        // the theme copy the value into the matching number field and fire a
        // change there itself; that copy arrives last, so recording it would
        // send focus to the number field instead of back to the slider.
        if (!e.isTrusted) return;
        // Remember which group it was in, so the list can reopen it, and note a
        // slider by its label: the range inputs have no id to look up.
        var group = el.closest(".m-accordion--item");
        var title = group ? group.querySelector(".m-accordion--item-button, .a11y-accordion-control") : null;
        lastFilter = {
          id: el.id,
          name: el.name,
          value: el.value,
          type: el.type,
          label: el.getAttribute("aria-label"),
          groupTitle: title ? title.textContent.replace(/\s+/g, " ").trim() : null,
        };
      },
      true
    );

    document.addEventListener("collection:rerendered", function () {
      var count = document.querySelectorAll("#CollectionProductGrid .m-product-item, [data-product-container] .m-product-item").length;
      window.a11yAnnounce("Product list updated. " + count + (count === 1 ? " product" : " products") + " shown.");
      if (!lastFilter) return;
      var filter = lastFilter;
      lastFilter = null;
      initAccordions();
      initCollapsibles();

      var target = null;
      if (filter.type === "range" && filter.label) {
        // A slider has no id; find it again by the name it announces
        target = document.querySelector('#CollectionFiltersForm input[type="range"][aria-label="' + CSS.escape(filter.label) + '"]');
      }
      if (!target && filter.id) target = document.getElementById(filter.id);
      if (!target && filter.name) {
        target = document.querySelector(
          '#CollectionFiltersForm [name="' + CSS.escape(filter.name) + '"][value="' + CSS.escape(filter.value) + '"]'
        );
      }
      if (!target) {
        syncCollapsedPanels();
        return;
      }

      // The rebuilt list collapses its groups again. Reopen the one holding the
      // control that was used, otherwise focus lands inside a closed panel and
      // the next few Tab presses have nothing to show on screen.
      var group = target.closest(".m-accordion--item");
      if (group && !group.classList.contains("open")) {
        var control = group.querySelector(".a11y-accordion-control, .m-accordion--item-button");
        if (control) control.click();
      }
      syncCollapsedPanels();

      // Reopening the panel above moves focus to the group's title, which is
      // itself focusable — that is the jump "back to the top of the filters"
      // people see. Putting focus back once, immediately, loses to the list
      // still rebuilding, so keep checking until it holds.
      var attempts = [0, 120, 350, 700, 1200];
      attempts.forEach(function (delay) {
        setTimeout(function () {
          if (!document.contains(target)) return;
          if (document.activeElement === target) return; // already there

          // The rebuilt list collapses its groups, and a collapsed panel is
          // marked inert to keep it out of the tab order (2.4.3). Focus inside
          // an inert subtree is refused silently, which is why every attempt to
          // put it back did nothing. Reopen the group on each try, not once
          // before them, and clear any inert left on the way to the control.
          var holder = target.closest(".m-accordion--item");
          if (holder && !holder.classList.contains("open")) {
            var reopen = holder.querySelector(".a11y-accordion-control, .m-accordion--item-button");
            if (reopen) reopen.click();
          }
          var node = target.parentElement;
          while (node && node !== document.body) {
            if (node.hasAttribute("inert") && node.closest("#CollectionFiltersForm")) node.removeAttribute("inert");
            node = node.parentElement;
          }

          target.focus({ preventScroll: true });
          // A control hidden behind a styled label may refuse focus. Fall back
          // to the label, which is what carries the ring anyway.
          if (document.activeElement !== target) {
            var label = (target.labels && target.labels[0]) || target.closest(".m-facet--checkbox");
            if (label) {
              if (!label.hasAttribute("tabindex")) label.setAttribute("tabindex", "-1");
              label.focus({ preventScroll: true });
            }
          }
        }, delay);
      });
    });

    var loadMoreCount = -1;
    document.addEventListener(
      "click",
      function (e) {
        if (e.target.closest && e.target.closest("[data-load-more]")) {
          loadMoreCount = document.querySelectorAll("[data-product-container] .m-product-item").length;
        }
      },
      true
    );
    if (window.MinimogEvents && typeof MinimogEvents.subscribe === "function") {
      MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", function () {
        if (loadMoreCount < 0) return;
        var items = document.querySelectorAll("[data-product-container] .m-product-item");
        var added = items.length - loadMoreCount;
        var firstNew = items[loadMoreCount];
        loadMoreCount = -1;
        if (added > 0) {
          window.a11yAnnounce(added + " more products loaded.");
          var link = firstNew && firstNew.querySelector("a[href]");
          if (link) link.focus();
        }
      });
    }
  }

  /* --------------------------------------------------------------------------
     Form errors in text, not in a pop-up bubble (3.3.1, 3.3.3)
     Left to itself the browser shows its own "Please fill in this field" bubble.
     It disappears after a few seconds, only ever reports the first problem, says
     nothing about which field it belongs to once it has gone, and each browser
     exposes it differently. Replace it with a message that stays on the page,
     is tied to its field, and is announced when it appears.
     -------------------------------------------------------------------------- */
  // Set by initFocusNotObscured; used by the form code to scroll a wrong field
  // into a comfortable reading position.
  var scrollIntoComfortableView = null;

  // While this stands, the focus handler leaves scrolling alone: the form code
  // has already placed the page deliberately and a second scroll on top of it
  // is what a reader sees as a jump.
  var suppressAutoScrollUntil = 0;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  /* --------------------------------------------------------------------------
     Contact and newsletter forms (3.3.1, 3.3.2, 3.3.3, 4.1.3)

     The submit guard is on the document, in the capture phase, rather than on
     each form. Binding per form was the flaw behind the reloads people kept
     seeing: any form we had not bound — one rendered after we looked at the
     page, one replaced when a section re-rendered, one that appeared while the
     script was still starting — submitted natively and took the whole page
     with it. A capture-phase listener on the document catches every such form
     however late it appears, and runs before the theme's own handlers.
     -------------------------------------------------------------------------- */
  var FORM_SELECTOR = ".m-contact-form form, form.m-newsletter__form, form[action*='/contact']";

  function isOurForm(form) {
    if (!form || form.tagName !== "FORM") return false;
    var matches = form.matches || form.msMatchesSelector || form.webkitMatchesSelector;
    if (!matches) return false;
    try {
      return matches.call(form, FORM_SELECTOR);
    } catch (error) {
      return false;
    }
  }

  function formFields(form) {
    return form.querySelectorAll("input:not([type=hidden]):not([type=submit]):not([type=button]), textarea, select");
  }

  function fieldLabel(field) {
    if (field.labels && field.labels[0]) return field.labels[0].textContent.replace(/\*/g, "").replace(/\s+/g, " ").trim();
    return field.getAttribute("aria-label") || field.getAttribute("placeholder") || "This field";
  }

  function messageFor(field) {
    var value = (field.value || "").trim();
    if (field.required && field.type === "checkbox" && !field.checked) return fieldLabel(field) + " is required.";
    if (field.required && !value) return fieldLabel(field) + " is required.";
    if (field.type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      // Lead with what is wrong before what to do: a screen reader reaches this
      // message mid-sentence, after the field's name and "invalid entry".
      return "Invalid input. Enter an email address in the format name@example.com.";
    }
    return null;
  }

  function errorId(field) {
    return (field.id || field.name || "field").replace(/[^\w-]/g, "-") + "-error";
  }

  // The block that holds the field and its label.
  //
  // This must never come back as the field itself. closest() starts at the
  // element, and on this theme the input's own class is "form-field
  // form-field--input", so closest(".form-field") returned the input — and the
  // error message was appended inside an <input>. A void element cannot have
  // children, so the browser kept the node and never drew it: the message was
  // in the DOM, and in the tests, but invisible on screen. Start the search at
  // the parent instead.
  function fieldWrapper(field) {
    var parent = field.parentElement;
    if (!parent) return field;
    // Attribute selectors for the theme's own classes: they contain a colon
    // ("m:column"), which is a pseudo-class in a selector and throws.
    var wrapper = null;
    try {
      wrapper = parent.closest(".form-field, .m-form-field, .field, [class*='__email'], [class*='__message'], [class*='m:column']");
    } catch (error) {
      wrapper = null;
    }
    return wrapper || parent;
  }

  function errorBox(field) {
    var id = errorId(field);
    var box = document.getElementById(id);
    if (!box) {
      box = document.createElement("p");
      box.id = id;
      box.className = "a11y-field-error";
      // The cross marks the message as an error without relying on colour
      // (1.4.1). It is a real element rather than CSS content because Chrome
      // folds ::before text into the accessible description, so the symbol was
      // being read out ahead of every message. Hidden from assistive tech, it
      // stays visible and stops being spoken.
      var mark = document.createElement("span");
      mark.className = "a11y-field-error__mark";
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = "✕";
      box.appendChild(mark);
      box.appendChild(document.createElement("span"));
      var anchor = fieldWrapper(field);
      // Put it straight after the field where possible, so it reads in the
      // right order and is never trapped inside a control.
      if (anchor === field.parentElement && field.nextSibling) anchor.insertBefore(box, field.nextSibling);
      else anchor.appendChild(box);
    }
    return box;
  }

  // The words live in the box's last span, so the hidden cross survives.
  function setErrorText(box, message) {
    var text = box.lastElementChild;
    if (!text) return;
    text.textContent = message || "";
    box.hidden = !message;
  }

  function errorTextOf(box) {
    return box && box.lastElementChild ? box.lastElementChild.textContent : "";
  }

  function clearError(field) {
    field.removeAttribute("aria-invalid");
    var box = document.getElementById(errorId(field));
    if (box) setErrorText(box, "");
    var described = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(function (token) {
      return token && token !== errorId(field);
    });
    if (described.length) field.setAttribute("aria-describedby", described.join(" "));
    else field.removeAttribute("aria-describedby");
  }

  function showError(field, message) {
    var box = errorBox(field);
    setErrorText(box, message);
    field.setAttribute("aria-invalid", "true");
    var described = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    if (described.indexOf(box.id) === -1) described.push(box.id);
    field.setAttribute("aria-describedby", described.join(" "));
  }

  // Space left above the field: enough to clear the sticky header and still
  // read the label sitting above the box.
  var FIELD_HEADROOM = 110;

  /* Put a field that has just been marked wrong in front of the reader, along
     with its label and the message now underneath it.

     In one movement. The position is worked out up front rather than scrolled
     at roughly and corrected afterwards, and focusing the field sets off the
     focus handler's own scroll, so that is held off while this settles.
     Together those were three scrolls competing over the same page, which
     reads as a jump. */
  function bringIntoView(field) {
    var wrapper = fieldWrapper(field);
    // Landing exactly on the field leaves its name above the top of the window,
    // so the reader sees a box outlined in red and nothing saying which box.
    var label = field.labels && field.labels[0];
    if (label && !wrapper.contains(label)) wrapper = label.parentElement || wrapper;

    var current = window.pageYOffset || document.documentElement.scrollTop || 0;
    var limit = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    var target = Math.min(Math.max(current + wrapper.getBoundingClientRect().top - FIELD_HEADROOM, 0), limit);

    suppressAutoScrollUntil = Date.now() + 1400;
    if (Math.abs(target - current) < 4) return;
    window.scrollTo({ top: target, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }

  // Mark every field that has a problem, and hand back the ones that do.
  function collectProblems(form) {
    var invalid = [];
    Array.prototype.forEach.call(formFields(form), function (field) {
      field.dataset.a11yTouched = "true";
      var message = messageFor(field);
      if (message) {
        showError(field, message);
        invalid.push(field);
      } else {
        clearError(field);
      }
    });
    return invalid;
  }

  function reportProblems(invalid) {
    var first = errorTextOf(errorBox(invalid[0]));
    window.a11yAnnounce(
      invalid.length === 1
        ? "There is a problem with the form. " + first
        : "There are " + invalid.length + " problems with the form. The first is: " + first,
      true
    );
    bringIntoView(invalid[0]);
    invalid[0].focus({ preventScroll: true });
  }

  /* Send without reloading the page.

     This posts the form into a hidden iframe rather than using fetch. The
     platform answers a fetch/XHR POST to /contact with 403 whatever the
     encoding, so a background fetch always failed and fell back to an ordinary
     submit — a whole-page reload. A form posted into an iframe is an ordinary
     form post as far as the server is concerned, so it is accepted, while the
     page being read stays where it is. The response is same-origin, so the
     outcome can be read back and reported in place. */
  var frameSeq = 0;

  function sendInBackground(form, event) {
    if (!form.action || !document.body) return false;
    event.preventDefault();

    var button = form.querySelector("[type=submit]");
    form.dataset.a11ySubmitting = "true";
    if (button) button.disabled = true;
    window.a11yAnnounce("Sending…");

    var frame = document.createElement("iframe");
    frame.name = "a11y-form-target-" + ++frameSeq;
    frame.title = "Form submission";
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    frame.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px";
    document.body.appendChild(frame);

    var settled = false;
    var sawInterstitial = false;
    var previousTarget = form.getAttribute("target");

    function cleanUp() {
      if (previousTarget === null) form.removeAttribute("target");
      else form.setAttribute("target", previousTarget);
      delete form.dataset.a11ySubmitting;
      setTimeout(function () {
        if (frame.parentNode) frame.parentNode.removeChild(frame);
      }, 0);
    }

    function giveUpAndReload() {
      if (settled) return;
      settled = true;
      cleanUp();
      form.submit(); // fires no submit event, so the guard does not see it
    }

    frame.addEventListener("load", function () {
      if (settled) return;
      var href = "";
      var doc = null;
      try {
        doc = frame.contentDocument;
        href = frame.contentWindow.location.href;
      } catch (error) {
        // Should not happen — the response is same-origin
      }
      // A new iframe reports its own empty document as loaded before the
      // response arrives. That is not an answer to anything.
      if (!href || href === "about:blank") return;

      var outcome = outcomeOf(doc, href);
      if (outcome === "unknown") {
        // An interstitial — a bot check, a redirect notice — rather than an
        // answer. These resolve themselves and load again, so wait for what
        // comes next instead of reporting the interstitial as a refusal.
        sawInterstitial = true;
        return;
      }

      settled = true;
      cleanUp();
      if (outcome === "sent") reportSent(form);
      else reportRejected(form, serverMessage(doc));
      if (button) button.disabled = false;
    });

    setTimeout(function () {
      if (settled) return;
      if (sawInterstitial) {
        // Something answered, so the message may well have reached the store.
        // Submitting again could send it twice, which is worse than not
        // knowing, so say plainly that it could not be confirmed.
        settled = true;
        cleanUp();
        if (button) button.disabled = false;
        reportUnconfirmed(form);
        return;
      }
      // Nothing ever answered, so nothing was submitted: it is safe to send it
      // the ordinary way rather than lose a real message.
      giveUpAndReload();
    }, 25000);

    form.setAttribute("target", frame.name);
    form.submit();
    // Submitting into a named frame hands it the focus in some browsers,
    // stranding a keyboard user on an invisible iframe. Keep focus here.
    if (button) button.focus({ preventScroll: true });
    return true;
  }

  // What the response actually says. Anything we cannot read as one of our own
  // pages is "unknown" rather than a refusal: an interstitial has no opinion
  // about the submission.
  function outcomeOf(doc, href) {
    if (/posted=true/.test(href)) return "sent";
    if (!doc) return "unknown";
    if (doc.querySelector("form[action*='/contact'], .shopify-section, #MainContent")) return "rejected";
    return "unknown";
  }

  // The server re-renders the page with its own message when it turns a
  // submission down. Pull that out so we can say the same thing.
  function serverMessage(doc) {
    if (!doc) return null;
    var candidates = doc.querySelectorAll(
      "[id*='newsletter-error'], .errors li, .errors, .form__message--error, .m-form__message--error, [class*='error-message']"
    );
    for (var i = 0; i < candidates.length; i++) {
      var text = (candidates[i].textContent || "").replace(/\s+/g, " ").trim();
      if (!text || text.length >= 200) continue;
      // The server's own wording is sometimes a fragment with no subject —
      // "can't be blank", "is invalid" — which tells the reader nothing on its
      // own. Say something that stands up by itself instead.
      if (/^(can'?t be blank|cannot be blank|is invalid|invalid|is required|required)\.?$/i.test(text)) return null;
      return text;
    }
    return null;
  }

  function insertNote(form, text, urgent) {
    var note = document.createElement("p");
    note.className = "a11y-form-success";
    note.setAttribute("role", "status");
    note.setAttribute("tabindex", "-1");
    note.textContent = text;
    form.parentNode.insertBefore(note, form);
    if (scrollIntoComfortableView) scrollIntoComfortableView(note);
    note.focus({ preventScroll: true });
    window.a11yAnnounce(text, !!urgent);
    return note;
  }

  function reportSent(form) {
    insertNote(
      form,
      form.closest(".m-contact-form") ? "Thanks — your message has been sent. We'll be in touch." : "Thanks — you're signed up.",
      false
    );
    form.hidden = true;
  }

  function reportUnconfirmed(form) {
    insertNote(
      form,
      "We could not confirm whether your message went through. Please check your email for a reply before sending it again, or call us.",
      true
    );
  }

  // The server turned it down. Say so next to the field it belongs to, rather
  // than reloading the page and replacing everything with "invalid".
  function reportRejected(form, message) {
    var field = form.querySelector("input[type=email]") || form.querySelector("input:not([type=hidden]):not([type=submit])");
    var text = message || "That was not accepted. Please check it and try again.";
    if (!field) {
      window.a11yAnnounce(text, true);
      return;
    }
    showError(field, text);
    bringIntoView(field);
    field.focus({ preventScroll: true });
    window.a11yAnnounce("There is a problem with the form. " + text, true);
  }

  // The one and only submit path, for every form of ours on the page.
  function handleSubmit(event) {
    var form = event.target;
    if (!isOurForm(form)) return;
    if (form.dataset.a11ySubmitting === "true") {
      event.preventDefault();
      return;
    }
    form.setAttribute("novalidate", "");
    var invalid = collectProblems(form);
    if (invalid.length) {
      event.preventDefault();
      event.stopPropagation();
      reportProblems(invalid);
      return;
    }
    sendInBackground(form, event);
  }

  function initFormValidation() {
    // The submit guard above is global; this only adds the per-field niceties:
    // tell someone as soon as a problem is fixed, but never scold mid-typing.
    Array.prototype.forEach.call(document.querySelectorAll(FORM_SELECTOR), function (form) {
      if (form.dataset.a11yValidate) return;
      form.dataset.a11yValidate = "true";
      form.setAttribute("novalidate", "");
      Array.prototype.forEach.call(formFields(form), function (field) {
        field.addEventListener("blur", function () {
          if (!field.dataset.a11yTouched) return;
          var message = messageFor(field);
          if (message) showError(field, message);
          else clearError(field);
        });
        field.addEventListener("input", function () {
          if (field.getAttribute("aria-invalid") === "true" && !messageFor(field)) clearError(field);
        });
      });
    });
  }

  /* --------------------------------------------------------------------------
     A collapsed panel must not keep its controls in the tab order (2.4.3)
     The theme collapses panels to zero height with the contents left visible to
     the browser, so a closed "Size" filter still holds six checkboxes that Tab
     walks straight through with nothing on screen to show for it.
     -------------------------------------------------------------------------- */
  function syncCollapsedPanels(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll(".m-accordion--item"), function (item) {
      var content = item.querySelector(".m-accordion--item-content");
      if (!content) return;
      var open = item.classList.contains("open");
      if (open === !content.hasAttribute("inert")) return;
      if (open) content.removeAttribute("inert");
      else content.setAttribute("inert", "");
    });
    Array.prototype.forEach.call(scope.querySelectorAll(".m-collapsible"), function (item) {
      var content = item.querySelector(".m-collapsible--content, [data-content]");
      if (!content) return;
      var open = !item.classList.contains("is-collapsed");
      if (open === !content.hasAttribute("inert")) return;
      if (open) content.removeAttribute("inert");
      else content.setAttribute("inert", "");
    });
  }

  function watchCollapsedPanels() {
    Array.prototype.forEach.call(document.querySelectorAll(".m-accordion--item, .m-collapsible"), function (item) {
      if (item.dataset.a11yCollapseWatch) return;
      item.dataset.a11yCollapseWatch = "true";
      onAttrChange(item, function () {
        syncCollapsedPanels(item.parentElement || document);
      });
    });
    syncCollapsedPanels();
  }

  /* --------------------------------------------------------------------------
     Every page needs a top-level heading (1.3.1, 2.4.6)
     Some pages are built entirely from sections whose headings start at H2, so
     there is nothing telling a screen reader what the page itself is. Where
     that happens, put the page's own title in as the H1. It is there for
     screen readers only, so the page looks exactly the same.
     -------------------------------------------------------------------------- */
  function ensurePageHeading() {
    if (document.querySelector("h1")) return;
    var main = document.getElementById("MainContent") || document.querySelector("main");
    if (!main) return;
    var title = (document.title || "").split("–")[0].split("|")[0].trim();
    if (!title) return;
    var heading = document.createElement("h1");
    heading.className = "m:visually-hidden";
    heading.textContent = title;
    main.insertBefore(heading, main.firstChild);
  }

  /* --------------------------------------------------------------------------
     A heading with nothing in it is not a heading (1.3.1)
     Text pasted in from a word processor brings anchor-only headings with it,
     such as <h1><a name="_faqa4vdfe2hp"></a></h1> in the policy pages. A screen
     reader announces "heading level 1" and then nothing, and the page appears
     to have two top-level headings. The policy text is store content shared
     with the live site, so drop the heading role here instead of editing it.
     -------------------------------------------------------------------------- */
  function hideEmptyHeadings(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll("h1, h2, h3, h4, h5, h6"), function (heading) {
      if (heading.dataset.a11yEmpty) return;
      if ((heading.textContent || "").trim()) return;
      // An image with a description is a legitimate heading; only empty ones go.
      if (heading.querySelector("img[alt]:not([alt=''])")) return;
      heading.dataset.a11yEmpty = "true";
      heading.setAttribute("role", "presentation");
    });
  }

  /* --------------------------------------------------------------------------
     The words on a control must appear in its name (2.5.3)
     Someone using voice control says the words they can see. Where the button
     reads "2 columns" but its name is "2-column", the spoken command matches
     nothing and the control cannot be operated by voice at all.
     -------------------------------------------------------------------------- */
  // Compare names on their letters and digits alone, so punctuation and spacing
  // do not decide whether one name contains another.
  function squashLabel(value) {
    return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  function syncVisibleLabels(root) {
    var scope = root || document;

    // The collection view-switch buttons keep their wording in a tooltip, which
    // is the only text a sighted person ever sees on them. Name them with it.
    Array.prototype.forEach.call(scope.querySelectorAll(".m-tooltip"), function (control) {
      var tip = control.querySelector(".m-tooltip__content");
      if (!tip) return;
      var text = (tip.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;
      // A name that already contains those words is left alone. It is often the
      // more useful one: the zoom buttons read "Zoom in on image 2 of 5" against
      // a tooltip saying only "Zoom in", and overwriting them left every zoom
      // button on the page with the same name (2.4.4, 4.1.2). Rename only where
      // the visible words are missing from the name altogether.
      if (squashLabel(control.getAttribute("aria-label")).indexOf(squashLabel(text)) !== -1) return;
      control.setAttribute("aria-label", text);
    });

    // The cart app names its discount button "Apply discount code" while the
    // button itself reads "Add", so "click Add" matches nothing. Named here
    // rather than generally: rewriting any mismatched label by rule produces
    // nonsense as often as it helps.
    Array.prototype.forEach.call(scope.querySelectorAll('button[aria-label="Apply discount code"]'), function (button) {
      if (/^add$/i.test((button.textContent || "").trim())) {
        button.setAttribute("aria-label", "Add discount code");
      }
    });
  }

  /* --------------------------------------------------------------------------
     Bold paragraphs used as headings in blog articles (1.3.1)
     Several articles set a section title as a bold paragraph rather than a
     heading. It looks like a heading, but to a screen reader it is an ordinary
     sentence, so the reader cannot jump between sections — which is the main
     way a long article gets navigated.

     The articles are store content, shared with the live site, so they are not
     ours to edit. Giving the paragraph the heading role instead says the same
     thing to a screen reader, changes nothing on screen, and leaves the text
     itself alone. If the articles are later fixed properly in the admin, real
     headings are skipped by this code and nothing collides.
     -------------------------------------------------------------------------- */
  function promoteFakeHeadings() {
    if (!/\/blogs\//.test(location.pathname)) return;

    var heading = document.querySelector("main h1, article h1, h1");
    var body = heading && heading.parentElement ? heading.parentElement : null;
    var article = document.querySelector(".m-article__content, .rte, article");
    var scope = article || body;
    if (!scope) return;

    // A paragraph counts as a heading only if it is wholly bold and reads like
    // a title. The exclusions matter more than the rule: the same markup is
    // used for table cells and for sentences that introduce a list.
    function looksLikeHeading(el) {
      if (el.closest("table, li, th, td, figcaption, blockquote, nav, footer, aside")) return false;
      var text = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (text.length < 3 || text.length > 100) return false;
      // "Consider:" and "…details can include:" lead into what follows;
      // they are sentences, not titles.
      if (/[:;,]$/.test(text)) return false;
      var bold = Array.prototype.filter.call(el.children, function (child) {
        return child.tagName === "STRONG" || child.tagName === "B";
      });
      if (!bold.length) return false;
      var boldText = bold
        .map(function (b) {
          return b.textContent;
        })
        .join("")
        .replace(/\s+/g, " ")
        .trim();
      if (boldText !== text) return false;
      // A heading introduces something. One with nothing after it is a caption.
      var next = el.nextElementSibling;
      return !!next && !/^H[1-6]$/.test(next.tagName);
    }

    // Sit one level below whatever real heading the paragraph falls under, so
    // the outline stays properly nested instead of everything becoming an H3.
    function levelFor(el) {
      var node = el;
      while (node) {
        var previous = node.previousElementSibling;
        while (previous) {
          var found = /^H([1-6])$/.exec(previous.tagName);
          if (found) return Math.min(parseInt(found[1], 10) + 1, 6);
          var inner = previous.querySelectorAll ? previous.querySelectorAll("h1,h2,h3,h4,h5,h6") : [];
          if (inner.length) {
            var last = /^H([1-6])$/.exec(inner[inner.length - 1].tagName);
            if (last) return Math.min(parseInt(last[1], 10) + 1, 6);
          }
          previous = previous.previousElementSibling;
        }
        node = node.parentElement;
        if (node === scope || !node) break;
      }
      return 3;
    }

    Array.prototype.forEach.call(scope.querySelectorAll("p"), function (el) {
      if (el.dataset.a11yHeading) return;
      if (!looksLikeHeading(el)) return;
      el.dataset.a11yHeading = "true";
      el.setAttribute("role", "heading");
      el.setAttribute("aria-level", String(levelFor(el)));
    });
  }

  /* --------------------------------------------------------------------------
     Bring an opened panel into view.
     Shared by both kinds of expanding section on the site. Centres the heading
     and its text when the whole thing fits on screen, otherwise starts it just
     below the sticky header so it can be read from the top.
     -------------------------------------------------------------------------- */
  function revealPanel(heading, content, stillOpen) {
    setTimeout(function () {
      if (stillOpen && !stillOpen()) return;
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--m-header-height"), 10);
      if (isNaN(headerHeight)) headerHeight = 80;
      var topLimit = headerHeight + 16;
      var available = viewport - topLimit - 16;
      var top = heading.getBoundingClientRect().top;
      var blockHeight = content.getBoundingClientRect().bottom - top;
      var wanted = blockHeight <= available ? topLimit + (available - blockHeight) / 2 : topLimit;
      var shift = top - wanted;
      if (Math.abs(shift) < 8) return;
      window.scrollBy({ top: shift, behavior: "smooth" });
    }, 450);
  }

  /* --------------------------------------------------------------------------
     Product page "Description" / "Delivery & Returns" panels.
     These are a different component from the accordions elsewhere: the button
     already reports its state but is not tied to the text it reveals, and the
     text unfolds below the fold when the button sits low on screen.
     -------------------------------------------------------------------------- */
  function initCollapsibles(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll(".m-collapsible"), function (item) {
      if (item.dataset.a11yCollapsible) return;
      item.dataset.a11yCollapsible = "true";
      var button = item.querySelector(".m-collapsible--button, [data-trigger]");
      var content = item.querySelector(".m-collapsible--content, [data-content]");
      if (!button || !content) return;

      if (!content.id) content.id = "a11y-panel-" + Math.random().toString(36).slice(2, 9);
      button.setAttribute("aria-controls", content.id);
      if (!button.getAttribute("aria-expanded")) {
        button.setAttribute("aria-expanded", String(!item.classList.contains("is-collapsed")));
      }

      var wasOpen = button.getAttribute("aria-expanded") === "true";
      onAttrChange(item, function () {
        var isOpen = !item.classList.contains("is-collapsed");
        button.setAttribute("aria-expanded", String(isOpen));
        if (isOpen && !wasOpen) {
          revealPanel(button, content, function () {
            return !item.classList.contains("is-collapsed");
          });
        }
        wasOpen = isOpen;
      });
    });
  }

  /* --------------------------------------------------------------------------
     The sticky "Add to cart" bar (2.4.3, 2.4.11)
     The bar is hidden by sliding it below the window rather than by hiding it,
     so its variant, quantity and Add to cart controls stay in the tab order the
     whole time. Tabbing past the product description then lands on five
     controls nobody can see. Take the bar out of the tab order whenever it is
     off screen, and put it back when it slides up.
     -------------------------------------------------------------------------- */
  function initStickyAddToCart() {
    var bar = document.querySelector(".m-sticky-addtocart");
    if (!bar) return;

    function sync() {
      var rect = bar.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      // Off screen if it sits at or below the bottom edge, or has no height
      var hidden = rect.height === 0 || rect.top >= viewport - 4;
      if (hidden === bar.hasAttribute("inert")) return;
      if (hidden) {
        // Don't strand focus inside the bar as it slides away
        if (bar.contains(document.activeElement)) document.activeElement.blur();
        bar.setAttribute("inert", "");
      } else {
        bar.removeAttribute("inert");
      }
    }

    onAttrChange(bar, sync);
    var ticking = false;
    window.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );
    window.addEventListener("resize", sync);
    sync();
  }

  /* --------------------------------------------------------------------------
     Nothing focused may sit behind a fixed bar (2.4.11 Focus Not Obscured)
     The sticky header at the top and the add-to-cart bar at the bottom both
     float above the page, so an element can take focus underneath them with no
     scrolling of its own. Nudge the page until the focused element is clear.
     -------------------------------------------------------------------------- */
  function initFocusNotObscured() {
    // How much of the top and bottom of the window is taken by floating bars
    function topBarDepth() {
      var header = document.querySelector("header.m-header__desktop, header.m-header__mobile");
      if (!header) return 0;
      var position = getComputedStyle(header).position;
      if (position !== "fixed" && position !== "sticky") return 0;
      var rect = header.getBoundingClientRect();
      return rect.top <= 0 ? Math.max(0, rect.bottom) : 0;
    }
    function bottomBarDepth() {
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var depth = 0;
      Array.prototype.forEach.call(document.querySelectorAll(".m-sticky-addtocart, .m-cart-drawer__footer, [class*='sticky-atc']"), function (bar) {
        if (bar.hasAttribute("inert")) return;
        var position = getComputedStyle(bar).position;
        if (position !== "fixed" && position !== "sticky") return;
        var rect = bar.getBoundingClientRect();
        if (rect.height && rect.top < viewport - 4) depth = Math.max(depth, viewport - rect.top);
      });
      return depth;
    }

    // A long filter list scrolls inside its own box, not with the page. Focus
    // can move to an option well outside that box while the box itself sits in
    // full view, so scrolling the window does nothing and the focus ring is
    // simply not on screen (2.4.11). Walk the scrollable ancestors first.
    // Custom checkboxes and radios hide the real control and show a styled
    // label, which is what carries the focus ring. Measuring the hidden input
    // sends the page to wherever that input happens to sit — often nowhere
    // near what the reader can see. Position by whatever is actually visible.
    function visibleProxy(el) {
      var rect = el.getBoundingClientRect();
      if (rect.width > 1 && rect.height > 1) return el;
      if (el.labels && el.labels[0] && el.labels[0].getBoundingClientRect().height > 1) return el.labels[0];
      var parent = el.parentElement;
      if (parent && parent.getBoundingClientRect().height > 1) return parent;
      return el;
    }

    // A product card reads as one thing: a picture, then the name under it.
    // Focus lands on the name, so placing the name alone at a comfortable
    // reading line pushes the picture off the top of the window — the reader
    // hears the product's name with its photograph out of sight. Where the
    // focused control belongs to a card, position the card.
    function focusUnit(el) {
      if (!el || !el.closest) return el;
      return el.closest(".m-product-card, .m-product-item, .m-collection-card") || el;
    }

    var scrolledInsideBox = false;

    function scrollWithinContainers(target) {
      scrolledInsideBox = false;
      var el = visibleProxy(target);
      var node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        var style = getComputedStyle(node);
        var scrolls = /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 2;
        if (scrolls) {
          var box = node.getBoundingClientRect();
          var item = el.getBoundingClientRect();
          // Move as little as possible: just inside the edge, with a little
          // room to show there is more beyond. Centring the option instead
          // threw the list a long way at the end of a run, and the page then
          // corrected it, which reads as a lurch down and back.
          var pad = 10;
          if (item.top < box.top + pad) node.scrollTop -= box.top + pad - item.top;
          else if (item.bottom > box.bottom - pad) node.scrollTop += item.bottom - box.bottom + pad;
          scrolledInsideBox = true;
        }
        node = node.parentElement;
      }
    }

    function ensureVisible(target) {
      scrollWithinContainers(target);
      var el = visibleProxy(target);
      var rect = el.getBoundingClientRect();
      if (!rect.height || !rect.width) return;
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var topBound = topBarDepth() + 12;
      var bottomBound = viewport - bottomBarDepth() - 12;
      var usable = bottomBound - topBound;
      if (usable < 40) return; // nowhere useful to put it

      // Measure the whole card instead of the link inside it, but only while
      // the card will fit: a card taller than the window has to fall back to
      // the control, or the control itself could end up out of sight.
      var unit = focusUnit(el);
      if (unit !== el) {
        var unitRect = unit.getBoundingClientRect();
        if (unitRect.height && unitRect.height <= usable) rect = unitRect;
      }

      // Landing on something at the very bottom of the window is technically
      // visible but uncomfortable: there is no sight of what follows it. Treat
      // the lower third as too low and lift it to a comfortable reading line,
      // about a third of the way down.
      var comfortLine = topBound + usable * 0.35;
      var tooLow = rect.top > topBound + usable * 0.66;

      var shift = 0;
      if (scrolledInsideBox) {
        // The list moved inside its own box a moment ago. Moving the page as
        // well gives two travels at once, which is what a lurch is. Only step
        // in if the item is genuinely outside the window.
        if (rect.bottom > bottomBound) shift = rect.bottom - bottomBound;
        else if (rect.top < topBound) shift = rect.top - topBound;
      } else if (tooLow && rect.height < usable * 0.6) {
        shift = rect.top - comfortLine;
      } else if (rect.bottom > bottomBound) {
        shift = rect.bottom - bottomBound;
      }
      // Never push the element's top up behind the header
      if (rect.top - shift < topBound) shift = rect.top - topBound;
      if (Math.abs(shift) < 6) return;
      window.scrollBy({ top: shift, behavior: "smooth" });
    }

    // Share the comfort-line scrolling with the form code, so a field that has
    // just been marked as wrong is brought into view the same way.
    scrollIntoComfortableView = ensureVisible;

    // A slider lays its pictures in a row and moves that row with a transform.
    // The browser knows nothing of this: finding focus on a picture off to one
    // side, it scrolls the container to reveal it. Hiding the overflow does not
    // stop that, it only hides the scrollbar — so the two disagree and the
    // reader is left looking at the join between two pictures. Ask the slider
    // to go to that picture instead, and undo the browser's scroll.
    // A looping slider has to be asked by the picture's real place: its own
    // list includes the copies it makes to seam the loop, so counting along
    // that list lands somewhere else entirely.
    function syncSlider(target) {
      if (!target || !target.closest) return;
      var slide = target.closest(".swiper-slide");
      if (!slide) return;
      var container = slide.closest(".swiper-container, .swiper");
      if (!container) return;
      // The browser does its own scrolling after this runs, so clearing the
      // container once here is too early to hold. Clear it again on the next
      // turn, and once more after any smooth scrolling has finished.
      [0, 60, 250].forEach(function (delay) {
        setTimeout(function () {
          if (!document.contains(container)) return;
          if (container.scrollLeft) container.scrollLeft = 0;
          if (container.scrollTop) container.scrollTop = 0;
        }, delay);
      });
      var swiper = container.swiper;
      if (!swiper) return;
      var recorded = slide.getAttribute("data-swiper-slide-index");
      if (recorded !== null && recorded !== "" && typeof swiper.slideToLoop === "function") {
        var real = parseInt(recorded, 10);
        if (!isNaN(real) && real !== swiper.realIndex) swiper.slideToLoop(real);
        return;
      }
      if (typeof swiper.slideTo !== "function" || !swiper.slides) return;
      for (var i = 0; i < swiper.slides.length; i++) {
        if (swiper.slides[i] !== slide) continue;
        if (i !== swiper.activeIndex) swiper.slideTo(i);
        return;
      }
    }

    // Someone using a mouse has already found what they clicked on: it is under
    // the pointer. Moving the page under them is at best pointless and at worst
    // ruins what they were doing — a click to start selecting the text of an
    // error message would slide the page away mid-selection. The comfortable
    // reading line is for people arriving by keyboard, who cannot see where
    // focus went until the page shows them.
    var lastPointerDownAt = 0;
    document.addEventListener(
      "pointerdown",
      function () {
        lastPointerDownAt = Date.now();
      },
      true
    );
    function focusCameFromPointer() {
      return Date.now() - lastPointerDownAt < 400;
    }

    document.addEventListener("focusin", function (event) {
      var el = event.target;
      if (!el || el === document.body || !el.getBoundingClientRect) return;
      syncSlider(el);
      if (focusCameFromPointer()) return;
      // Controls that live inside a floating panel travel with it
      if (el.closest("[role='dialog'], .m-sticky-addtocart, .m-menu-drawer, .m-search-popup, header.m-header__desktop, header.m-header__mobile")) return;
      // Check twice: a bar can slide into place after the browser has scrolled
      setTimeout(function () {
        if (Date.now() < suppressAutoScrollUntil) return;
        if (document.activeElement === el) ensureVisible(el);
      }, 80);
      setTimeout(function () {
        if (Date.now() < suppressAutoScrollUntil) return;
        if (document.activeElement === el) ensureVisible(el);
      }, 600);
    });
  }

  /* --------------------------------------------------------------------------
     Focus rings that would be clipped (2.4.7, 2.4.11)
     A ring is painted just outside the element, so any ancestor that scrolls or
     hides its overflow — carousels, the tab strip, scrolling filter lists,
     accordion panels — cuts it off. On focus, check whether that is happening
     and, if so, draw the ring inside the element instead. Also scroll the
     element into view inside its container, so a focused item in a horizontal
     strip isn't left off screen.
     -------------------------------------------------------------------------- */
  function initFocusRingFallback() {
    function isClipped(el) {
      var rect = el.getBoundingClientRect();
      if (!rect.width && !rect.height) return false;
      var node = el.parentElement;
      for (var depth = 0; depth < 8 && node && node !== document.body; depth++, node = node.parentElement) {
        var style = getComputedStyle(node);
        var overflow = style.overflow + style.overflowX + style.overflowY;
        if (!/hidden|auto|scroll|clip/.test(overflow)) continue;
        var box = node.getBoundingClientRect();
        // The ring needs about 5px of room on every side
        if (
          rect.left - box.left < 5 ||
          box.right - rect.right < 5 ||
          rect.top - box.top < 5 ||
          box.bottom - rect.bottom < 5
        ) {
          return true;
        }
      }
      return false;
    }

    // An image that fills its link paints over the ring, because a positioned or
    // transformed child is drawn after its parent's outline. Photo tiles like
    // the "SOLID SHIRTS" cards are exactly that, so the ring has to go on top of
    // the picture rather than behind it.
    function ringWouldBeHidden(el) {
      var rect = el.getBoundingClientRect();
      if (rect.width < 24 || rect.height < 24) return false;
      var kids = el.querySelectorAll("img, picture, video, canvas, svg, responsive-image, [style*='transform']");
      for (var i = 0; i < kids.length; i++) {
        var style = getComputedStyle(kids[i]);
        if (style.position === "static" && style.transform === "none") continue;
        var box = kids[i].getBoundingClientRect();
        if (box.width >= rect.width - 8 && box.height >= rect.height - 8) return true;
      }
      return false;
    }

    function addOverlay(el) {
      if (el.querySelector(":scope > .a11y-focus-overlay")) return;
      if (getComputedStyle(el).position === "static") el.classList.add("a11y-ring-host");
      var overlay = document.createElement("span");
      overlay.className = "a11y-focus-overlay";
      overlay.setAttribute("aria-hidden", "true");
      el.appendChild(overlay);
    }
    function removeOverlay(el) {
      var overlay = el.querySelector(":scope > .a11y-focus-overlay");
      if (overlay) overlay.parentNode.removeChild(overlay);
      el.classList.remove("a11y-ring-host");
    }

    document.addEventListener("focusin", function (event) {
      var el = event.target;
      if (!el || el === document.body || !el.classList) return;
      if (ringWouldBeHidden(el)) {
        el.classList.add("a11y-ring-inset");
        addOverlay(el);
      } else if (isClipped(el)) {
        el.classList.add("a11y-ring-inset");
      }
      if (el.scrollIntoView && (el.classList.contains("a11y-ring-inset") || isClipped(el))) {
        // Never scroll sideways inside the mobile menu: its panels sit next to
        // each other and slide across, so scrolling the drawer horizontally
        // drags the open panel off the left edge. Note that leaving `inline`
        // out does not avoid this — it defaults to "nearest" — so inside the
        // drawer the call is skipped altogether.
        if (!el.closest(".m-menu-drawer, .m-megamenu-mobile")) {
          el.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
      }
    });
    document.addEventListener("focusout", function (event) {
      var el = event.target;
      if (!el || !el.classList) return;
      el.classList.remove("a11y-ring-inset");
      if (el.querySelector) removeOverlay(el);
    });
  }

  /* --------------------------------------------------------------------------
     Enter on a filter option (2.1.1 in practice)
     Filters are checkboxes, where the platform key is Space. Most people reach
     for Enter, find nothing happens, and conclude the filters are broken — so
     accept Enter as well. Space keeps working exactly as before.
     -------------------------------------------------------------------------- */
  function initFilterEnterKey() {
    document.addEventListener("keydown", function (event) {
      if (event.key !== "Enter") return;
      var el = event.target;
      if (!el || !el.matches) return;
      if (!el.matches('input[type="checkbox"], input[type="radio"]')) return;
      if (!el.closest(".m-facets, .m-filter--widget, #CollectionFiltersForm, .m-collection-filters, form[id*='Filter' i]")) return;
      event.preventDefault();
      el.click();
    });
  }

  /* --------------------------------------------------------------------------
     Enter on a size or colour swatch must not add to the cart (3.2.2)
     The swatches are radio buttons inside the add-to-cart form, so Enter counts
     as submitting the form. Choosing a size should never buy the item; arrow
     keys still move between options.
     -------------------------------------------------------------------------- */
  function initVariantEnterKey() {
    document.addEventListener(
      "keydown",
      function (event) {
        if (event.key !== "Enter") return;
        var el = event.target;
        if (!el || !el.matches) return;
        if (el.matches('.m-product-option__input, variant-picker input[type="radio"], .m-product-option--node input[type="radio"]')) {
          event.preventDefault();
        }
      },
      true
    );
  }

  /* --------------------------------------------------------------------------
     Cart drawer (app-rendered)
     The drawer marks itself role="dialog" aria-modal="true" and moves focus in,
     but the page behind it stays in the tab order and Escape does nothing.
     Make the background inert while it is open, close on Escape, and put focus
     back on the cart button afterwards.
     -------------------------------------------------------------------------- */
  function initAppCartDrawer() {
    var openPanel = null;
    var returnTo = null;

    function closeButton(panel) {
      return (
        panel.querySelector('button[aria-label*="close" i]') ||
        panel.querySelector("button.ks-modal-close, .ks-modal-close, button[class*='close' i], [data-close]")
      );
    }
    function setBackgroundInert(panel, on) {
      Array.prototype.forEach.call(document.body.children, function (child) {
        if (child.contains(panel)) return;
        if (child.id === "a11y-status" || child.id === "a11y-alert") return;
        if (on) {
          if (!child.hasAttribute("inert")) {
            child.setAttribute("inert", "");
            child.setAttribute("data-a11y-inert", "");
          }
        } else if (child.hasAttribute("data-a11y-inert")) {
          child.removeAttribute("inert");
          child.removeAttribute("data-a11y-inert");
        }
      });
    }

    // Any modal an app opens: the cart drawer, the size chart, the sign-up
    // pop-up. They all announce themselves the same way, and all need the same
    // treatment, so handle them by what they are rather than by name. A panel
    // with no way to close it is left alone — sealing the page around it would
    // strand the visitor.
    function findOpenDrawer() {
      var found = null;
      var topMost = -1;
      var panels = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        if (p.hasAttribute("data-a11y-overlay")) continue; // one of ours, already handled
        // The marketing pop-up manages its own focus and can appear unbidden at
        // any moment; sealing the page around it would interrupt whatever the
        // visitor was doing.
        if (p.className && /kl-private-reset|klaviyo/i.test(p.className.toString())) continue;
        if (!isVisible(p)) continue;
        if (p.getBoundingClientRect().width < 200) continue; // teasers and badges
        if (!closeButton(p)) continue;
        // When one opens on top of another (a size chart over the cart drawer),
        // the one in front is the one to manage.
        var z = parseInt(getComputedStyle(p).zIndex, 10);
        if (isNaN(z)) z = 0;
        if (z >= topMost) {
          topMost = z;
          found = p;
        }
      }
      return found;
    }

    function sync() {
      var panel = findOpenDrawer();
      if (panel && panel !== openPanel) {
        // Go back to whatever opened it — the size-guide link, a swatch, the
        // Add to cart button — rather than always the top of the page. Prefer
        // the last focus from outside a panel: the cart drawer focuses itself
        // as it opens, so lastFocused is the drawer by the time we get here.
        var opener = lastFocusedOutsidePanel || lastFocused;
        if (!opener || panel.contains(opener) || !document.contains(opener) || !isVisible(opener)) {
          opener = Array.prototype.filter.call(document.querySelectorAll("a.m-cart-icon-bubble"), isVisible)[0] || null;
        }
        returnTo = opener;
        openPanel = panel;
        setBackgroundInert(panel, true);

        // Several of these panels never move focus off the page behind them.
        if (!panel.contains(document.activeElement)) {
          var first = focusables(panel)[0] || closeButton(panel) || panel;
          if (first === panel && !panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1");
          setTimeout(function () {
            if (openPanel === panel) first.focus();
          }, 120);
        }
      } else if (!panel && openPanel) {
        setBackgroundInert(openPanel, false);
        openPanel = null;
        // The drawer's own close handler blurs afterwards, so claim focus back
        // a couple of times before giving up.
        var target = returnTo;
        returnTo = null;
        [0, 150, 400].forEach(function (delay) {
          setTimeout(function () {
            var active = document.activeElement;
            if (!target || !document.contains(target) || !isVisible(target)) return;
            if (!active || active === document.body) target.focus();
          }, delay);
        });
      }
    }

    document.addEventListener(
      "keydown",
      function (event) {
        if (event.key !== "Escape" || !openPanel) return;
        var close = closeButton(openPanel);
        if (close) {
          event.stopPropagation();
          close.click();
          setTimeout(sync, 300);
        }
      },
      true
    );

    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        sync();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "aria-hidden", "hidden"] });
    sync();
  }

  /* --------------------------------------------------------------------------
     The marketing pop-up (2.4.3)
     It opens on its own and takes the keyboard with it. We deliberately don't
     seal the page around it — it can appear at any moment and being trapped
     would be worse — but when it closes, focus is simply dropped on the floor,
     leaving the next Tab to start again from the top of the page. Put people
     back where they were.
     -------------------------------------------------------------------------- */
  function initMarketingPopup() {
    var wasOpen = false;
    var returnTo = null;
    var returnScroll = 0;

    function popup() {
      var dialogs = document.querySelectorAll("div.needsclick[role='dialog'], [class*='kl-private-reset'][role='dialog']");
      for (var i = 0; i < dialogs.length; i++) {
        if (isVisible(dialogs[i]) && dialogs[i].getBoundingClientRect().width > 120) return dialogs[i];
      }
      return null;
    }

    function sync() {
      var open = popup();
      if (open && !wasOpen) {
        wasOpen = true;
        var candidate = lastFocused;
        returnTo = candidate && !open.contains(candidate) && document.contains(candidate) ? candidate : null;
        // The pop-up locks scrolling by fixing the body in place, which loses
        // where the reader had got to: when it unlocks, the page is back at the
        // top. Remember the position so it can be put back.
        returnScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
      } else if (!open && wasOpen) {
        wasOpen = false;
        var target = returnTo;
        var scrollBack = returnScroll;
        returnTo = null;
        returnScroll = 0;
        // Put the page back where it was, but only if it actually jumped —
        // never fight someone who has scrolled somewhere themselves.
        if (scrollBack > 40) {
          [0, 120, 350].forEach(function (delay) {
            setTimeout(function () {
              var now = window.pageYOffset || document.documentElement.scrollTop || 0;
              if (now < 40) window.scrollTo(0, scrollBack);
            }, delay);
          });
        }
        if (!target) return;
        [0, 200, 500].forEach(function (delay) {
          setTimeout(function () {
            var active = document.activeElement;
            if (active && active !== document.body) return;
            if (document.contains(target) && isVisible(target)) target.focus({ preventScroll: true });
          }, delay);
        });
      }
    }

    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        sync();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "hidden"] });
    sync();
  }

  /* --------------------------------------------------------------------------
     Keep the cart button's name in step with the count, which the cart app
     changes without reloading the page (4.1.2).
     -------------------------------------------------------------------------- */
  function initCartCountLabel() {
    var link = document.querySelector("a.m-cart-icon-bubble");
    var count = link && link.querySelector("m-cart-count");
    if (!link || !count) return;
    var update = function () {
      var n = parseInt((count.textContent || "").replace(/\D/g, ""), 10);
      if (isNaN(n)) return;
      link.setAttribute("aria-label", "Cart, " + n + " item" + (n === 1 ? "" : "s"));
    };
    new MutationObserver(update).observe(count, { childList: true, characterData: true, subtree: true });
  }

  /* Marketing pop-up images are decorative; without alt they are read as filenames */
  function fixVendorImages(root) {
    var selector =
      "img[class*='kl-private-reset-css']:not([alt])," +
      "[class*='klaviyo-form'] img:not([alt])," +
      "div.needsclick[role='dialog'] img:not([alt])";
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), function (img) {
      img.setAttribute("alt", "");
    });
  }

  /* --------------------------------------------------------------------------
     Two carousels on one page must not share a name (1.3.1). Image-only
     slideshows have no heading to borrow, so number the repeats.
     -------------------------------------------------------------------------- */
  function dedupeCarouselLabels() {
    var seen = {};
    Array.prototype.forEach.call(document.querySelectorAll('[aria-roledescription="carousel"][aria-label]'), function (el) {
      var label = el.getAttribute("aria-label");
      seen[label] = (seen[label] || 0) + 1;
      if (seen[label] > 1) el.setAttribute("aria-label", label + " " + seen[label]);
    });
  }

  /* --------------------------------------------------------------------------
     Scrolling promotion bar (2.2.2)
     The marquee loops for ever and only pauses on hover, which keyboard and
     touch users can't do. Give each bar its own Pause / Play button.
     -------------------------------------------------------------------------- */
  function initScrollingPromotion() {
    var reduced = prefersReducedMotion();
    Array.prototype.forEach.call(document.querySelectorAll("m-scrolling-promotion:not([data-a11y-pause])"), function (bar) {
      if (!bar.querySelector(".m-promotion--animated")) return;
      bar.setAttribute("data-a11y-pause", "");
      var section = bar.closest(".m-scrolling-promotion-section") || bar.parentNode;
      section.classList.add("m-scrolling-promotion--has-pause");

      var button = document.createElement("button");
      button.type = "button";
      button.className = "m-marquee-pause";
      button.innerHTML =
        '<svg class="m-marquee-pause__icon-pause" width="12" height="14" viewBox="0 0 12 14" aria-hidden="true" focusable="false"><rect x="1" y="1" width="3.5" height="12" fill="currentColor"/><rect x="7.5" y="1" width="3.5" height="12" fill="currentColor"/></svg>' +
        '<svg class="m-marquee-pause__icon-play" width="12" height="14" viewBox="0 0 12 14" aria-hidden="true" focusable="false"><path d="M1 1l10 6-10 6z" fill="currentColor"/></svg>';

      function setPaused(paused) {
        section.classList.toggle("m-scrolling-promotion--a11y-paused", paused);
        button.classList.toggle("is-paused", paused);
        button.setAttribute("aria-label", paused ? "Play scrolling announcements" : "Pause scrolling announcements");
      }
      button.addEventListener("click", function () {
        setPaused(!section.classList.contains("m-scrolling-promotion--a11y-paused"));
      });
      // Someone who asked for less motion gets it stopped from the start
      setPaused(reduced);
      section.appendChild(button);
    });
  }

  /* --------------------------------------------------------------------------
     Links that open a new tab say so (3.2.5 / G201), including links added later
     -------------------------------------------------------------------------- */
  function annotateNewTabLinks(root) {
    var NOTE = "opens in a new tab";
    Array.prototype.forEach.call((root || document).querySelectorAll('a[target="_blank"]:not([data-a11y-newtab])'), function (link) {
      link.setAttribute("data-a11y-newtab", "");
      var label = link.getAttribute("aria-label");
      if (label) {
        if (label.toLowerCase().indexOf("new tab") === -1 && label.toLowerCase().indexOf("new window") === -1) {
          link.setAttribute("aria-label", label + ", " + NOTE);
        }
      } else if (!/new (tab|window)/i.test(link.textContent)) {
        var hint = document.createElement("span");
        hint.className = "m:visually-hidden";
        hint.textContent = " (" + NOTE + ")";
        link.appendChild(hint);
      }
    });
  }

  function watchNewContent() {
    var pending = false;
    new MutationObserver(function (mutations) {
      if (pending) return;
      var added = mutations.some(function (m) {
        return m.addedNodes.length > 0;
      });
      if (!added) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        annotateNewTabLinks();
        fixVendorImages();
        initAccordions();
        initCollapsibles();
        watchCollapsedPanels();
        initFormValidation();
        initProductMedia();
        syncQuantityButtons();
        syncVisibleLabels();
        hideEmptyHeadings();
      }, 300);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Server-rendered form errors / success messages get focus so they are read first */
  function focusFormMessages() {
    var message = document.querySelector("[data-a11y-focus-on-load]");
    if (message) {
      message.scrollIntoView({ block: "center" });
      message.focus({ preventScroll: true });
    }
  }

  function init() {
    initMobileMenu();
    initDesktopMenu();
    initSearchPopup();
    initAccordions();
    initThemeNotifications();
    syncQuantityButtons();
    initProductMedia();
    initCartAddons();
    initCollectionUpdates();
    initScrollingPromotion();
    dedupeCarouselLabels();
    ensurePageHeading();
    promoteFakeHeadings();
    syncVisibleLabels();
    hideEmptyHeadings();
    initCollapsibles();
    watchCollapsedPanels();
    initFormValidation();
    initStickyAddToCart();
    initFocusNotObscured();
    initFocusRingFallback();
    initFilterEnterKey();
    initVariantEnterKey();
    initAppCartDrawer();
    initMarketingPopup();
    initCartCountLabel();
    fixVendorImages();
    annotateNewTabLinks();
    watchNewContent();
    focusFormMessages();
  }

  // Wrap fetch immediately so requests made before DOMContentLoaded handlers are covered
  initCartAnnouncements();

  // Guard every form submission from the moment this script runs, not from
  // DOMContentLoaded. A form submitted in between would otherwise go straight
  // to the server and reload the page.
  document.addEventListener("submit", handleSubmit, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
